import { useEffect, useState } from 'react';
import { useFreemium } from '../hooks/useFreemium';

/**
 * CheckoutReturnHandler - Detects when user returns from LemonSqueezy checkout
 *
 * Handles:
 * - ?checkout=success → Show success message, refresh subscription
 * - ?checkout=canceled → Show cancellation message
 */
export function CheckoutReturnHandler() {
  const { checkSubscription } = useFreemium();
  const [message, setMessage] = useState<{ type: 'success' | 'info'; text: string } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');

    if (checkoutStatus === 'success') {
      console.log('[CheckoutReturnHandler] Payment successful! Refreshing subscription...');

      // Show success message
      setMessage({
        type: 'success',
        text: '✅ Payment successful! Unlocking Pro features...'
      });

      // Refresh subscription status to get updated tier
      checkSubscription();

      // Clean URL (remove ?checkout=success)
      window.history.replaceState(null, '', window.location.pathname);

      // Hide message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    }

    if (checkoutStatus === 'canceled') {
      console.log('[CheckoutReturnHandler] Checkout canceled');

      // Show cancellation message
      setMessage({
        type: 'info',
        text: 'Checkout canceled. No charges were made.'
      });

      // Clean URL
      window.history.replaceState(null, '', window.location.pathname);

      // Hide message after 4 seconds
      setTimeout(() => setMessage(null), 4000);
    }
  }, [checkSubscription]);

  if (!message) return null;

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
      <div
        className={`
          px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3
          ${message.type === 'success' ? 'bg-teal-600 text-white' : 'bg-blue-600 text-white'}
        `}
      >
        {message.type === 'success' ? (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
        <span className="font-medium">{message.text}</span>
      </div>
    </div>
  );
}
